import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import ApiService from "../../api/ApiService";
import BackEndService from "../../api/BackEndService";
import UserService from "../../api/UserService";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import MultiDocumentUploader from "../../components/DocumentUploader/MultipleDocumentUploader";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { Info } from "../../components/Info/Info";
import Modal from "../../components/Modal/Modal";
import MultipleObjectSelect from "../../components/MultipleObjectSelect/MultipleObjectSelect";
import { TabTypeSelection } from "../../components/TabTypeSelection/TabTypeSelection";
import { AccountCircleIcon } from "../../icons/AccountCircleIcon/AccountCircleIcon";
import { DeleteIcon } from "../../icons/DeleteIcon/DeleteIcon";
import { EditUserIcon } from "../../icons/EditUserIcon/EditUserIcon";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { userManagementActions } from "../../store/user-management-slice";
import { ROLES } from "../../store/user-slice";
import styles from "../../styles/add-sbk-user.module.css";
import styles_customer from "../../styles/user-management.module.css";
import { User } from "../../types/user";
import {
  mapRoleDisplay,
  mapRoleDisplayToRole,
} from "../../utils/mapRoleDisplay";

import { ChooseMultipleObject } from "./ChooseMultipleObject";

export const AddUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userState = location.state;
  const isCustomerAdminFromStore = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const isCustomerAdmin = userState.customerAdmin || isCustomerAdminFromStore;

  const customerId = useSelector(
    (state: RootState) => state.user.currentUser.customerId
  );
  const { selectedCustomer } = useSelector(
    (state: RootState) => state.userManagement
  );
  const customerAdmin = useSelector((state: RootState) => state.user);

  const roles = !isCustomerAdmin
    ? ["Techniker", "Admin"]
    : ["Kunde Admin", "Objektbetreuer"];

  const addSBKUser = userState?.addSBKUser;
  const initialFiles =
    userState?.documents?.map((doc: any) => ({
      id: doc.id,
      uuid: doc.mediaUuid,
      name: doc.fileName,
    })) || [];

  const [user, setUser] = useState<User>({
    firstName: userState?.firstName || "",
    lastName: userState?.lastName || "",
    email: userState?.email || "",
    roles:
      userState?.roles ?? (isCustomerAdmin ? ["Kunde Admin"] : ["Techniker"]),
    assignedProperties: userState?.assignedProperties,
  });
  const deactivated = userState?.deactivated;

  const [mappedResponses, setMappedResponses] = useState<any[]>([]);
  const [idForDelete, setIdForDelete] = useState<string[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [reactivateModal, setReactivateModal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [choose, setChoose] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState<any>(
    user.assignedProperties || []
  );
  const [discardModal, setDiscardModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRoleChange = (tab: string) => {
    setUser((prev) => ({ ...prev, role: tab, roles: [tab] }));
  };

  useEffect(() => {
    setUser((prev) => ({ ...prev, assignedProperties: selectedProperties }));
  }, [selectedProperties]);

  const handleMultipleFilesUpload = async (files: File[]) => {
    setLoading(true);
    const formDataArray = files.map((file) => {
      const formData = new FormData();
      formData.append("uuid", uuidv4());
      formData.append("name", file.name);
      formData.append("file", file);
      return formData;
    });

    try {
      const responses = await Promise.all(
        formDataArray.map((formData) => BackEndService.UploadImage(formData))
      );

      const mapped = responses.map((uploadedDocument: any) => ({
        uuid: uploadedDocument.uuid,
        name: uploadedDocument.name,
      }));
      setMappedResponses(mapped);
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteDocuments = (id: string) => {
    setIdForDelete((prevIds) => [...prevIds, id]);
  };

  const handleAddDocuments = async (customerId: string) => {
    setLoading(true);
    try {
      const payloads = mappedResponses.map((documentUpload) => ({
        type: "Document",
        documentType: "userUpload",
        fileName: documentUpload.name,
        isInternal: true,
        media: documentUpload.uuid,
        allowedRoles: [],
        attachedTo: {
          type: "Employee",
          id: customerId,
        },
      }));

      const addDocumentPromises = payloads.map((documentPayload) =>
        BackEndService.AddDocument(documentPayload)
      );

      await Promise.all(addDocumentPromises);
    } catch (error: any) {
      setError(error.error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseDeleteModal = () => {
    setDeleteUserModal(false);
  };
  const handleDeleteFiles = async () => {
    if (!idForDelete.length) return;

    try {
      const deletePromises = idForDelete.map((id) =>
        ApiService.Delete(id, `documents`)
      );

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting files:", error);
    }
  };

  const handleReactivateModalOpen = () => {
    setReactivateModal(true);
  };
  const handleReactivateModalClose = () => {
    setReactivateModal(false);
  };
  const reactivateUser = async () => {
    setReactivateModal(false);

    await UserService.ReactivateUser(
      isCustomerAdmin ? "customer-users" : "employees",
      userState?.id
    );
    sessionStorage.setItem("user", JSON.stringify(user));
    navigate("/user-management", {
      state: {
        customerDeleted: true,
      },
    });
  };

  const createSBKUser = async () => {
    setIsModalOpen(false);
    setLoading(true);
    const valuesArray = selectedProperties.map(
      (property: any) => property.value ?? property
    );
    const data: any = {
      customerId: userState.customerAdmin
        ? generateUUIDFromID(selectedCustomer?.id.toString()!)
        : isObjektbetreuer || isCustomerAdminFromStore
        ? customerId
        : null,
      firstName: user.firstName,
      lastName: user.lastName,
      authSettings: {
        hasLogin: true,
        loginBlocked: false,
        loginBlockedReason: null,
        loginName: user.email,
        roles: [mapRoleDisplayToRole(user.roles?.[0] ?? "")],
        accessAllProperties: false,
        assignedProperties:
          user.roles?.[0] === "Objektbetreuer" && selectedProperties
            ? valuesArray
            : [],

        ...(isCustomerAdmin && { isTechnician: true }),
      },
    };

    try {
      if (isCustomerAdmin || isObjektbetreuer) {
        if (editMode) {
          await UserService.EditCustomerUser(userState?.id, data);
          dispatch(
            userManagementActions.editCustomerUser({
              ...user,
              id: userState?.id,
            })
          );
        } else await UserService.CreateCustomerUser(data);
      } else {
        const response = !addSBKUser
          ? await UserService.EditSBKUser(userState?.id, data)
          : await UserService.CreateSBKUser(data);

        if (!addSBKUser) {
          await handleDeleteFiles();
        }
      
        await handleAddDocuments(response?.data.id);
      }

      sessionStorage.setItem("user", JSON.stringify(user));
      navigate("/user-management", {
        state: {
          customerDeleted: true,
        },
      });
    } catch (error) {
      setError("E-mail ist bereits vergeben.");
      setLoading(false);
    }
  };

  const deleteSBKUser = async () => {
    const data = {
      authSettings: {
        loginBlocked: true,
        loginBlockedReason: null,
      },
    };

    try {
      isCustomerAdmin
        ? await UserService.DeleteCustomerUser(userState?.id, data)
        : await UserService.DeleteSBKUser(userState?.id, data);

      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("deleted", "true");
      navigate("/user-management", {
        state: {
          customerDeleted: true,
        },
      });
    } catch (error: any) {
      setError(error?.message || "Error while deleting user");
    } finally {
      setDeleteUserModal(false);
    }
  };

  useEffect(() => {
    setError("");
  }, [user.firstName, user.lastName, user.email]);

  const isFormValid =
    user.lastName !== "" &&
    user.firstName !== "" &&
    user.email !== "" &&
    (user.roles?.[0] !== "Objektbetreuer" || selectedProperties.length > 0);

  const isObjektbetreuer = user.roles?.includes(ROLES.PROPERTY_MANAGER);

  return (
    <AppLayout
      withBackButton={true}
      hideHeader={isCustomerAdmin}
      onGoBack={() => {
        choose ? setChoose(false) : navigate(-1);
      }}
    >
      <Modal
        isOpen={isModalOpen}
        message={t("CreateUserModalMessage")}
        onConfirm={createSBKUser}
        onCancel={handleCloseModal}
        cancelText="Cancel"
        confirmText={"Erstellen"}
        backButtonShow={handleCloseModal}
        closeButtonColor={"#3D5170"}
        iconType={"create-sbk-user"}
      />
      <Modal
        isOpen={deleteUserModal}
        message={t("DeleteUserModalMessage")}
        onConfirm={deleteSBKUser}
        onCancel={handleCloseDeleteModal}
        cancelText={"Cancel"}
        confirmText={"Benutzer deaktivieren"}
        backButtonShow={handleCloseDeleteModal}
        confirmButtonColor={"#CB3844"}
        closeButtonColor={"#3D5170"}
        iconType={"remove"}
      />
      <Modal
        isOpen={reactivateModal}
        message={t("ReactivateUserModalMessage")}
        onConfirm={reactivateUser}
        onCancel={handleReactivateModalClose}
        cancelText="Cancel"
        confirmText={"Erstellen"}
        backButtonShow={handleReactivateModalClose}
        closeButtonColor={"#3D5170"}
        iconType={"create-sbk-user"}
      />
      <Modal
        isOpen={discardModal}
        message={t("DiscardChangesModalMessage")}
        onConfirm={() => {
          setDiscardModal(false);
        }}
        onCancel={() => {
          navigate(-1);
        }}
        cancelText={"Änderungen verwerfen"}
        confirmText={"Änderungen speichern"}
        backButtonShow={() => {
          setDiscardModal(false);
        }}
        iconType="alert"
      />
      <div className={styles_customer["customer-um"]}>
        <div className={styles_customer["customer-um__heading"]}>
          <p className={styles_customer["customer-um__title"]}>
            {isCustomerAdmin && (
              <LeftArrowIcon
                handleBackButtonClicked={() => {
                  editMode ? setDiscardModal(true) : navigate(-1);
                }}
              />
            )}
            {t("CreateUser")}
          </p>
        </div>
        {error && <Info type="error">{error}</Info>}
        {!choose && (
          <div className={`${styles["card-wrapper"]}`}>
            <div
              className={`${styles["card"]} ${
                editMode ? styles["edit-mode"] : ""
              }`}
            >
              <div className={styles["card__flex"]}>
                <p className={styles["card__p"]}>persönliche Angaben</p>
                <AccountCircleIcon color="green" />
              </div>
              <div className={styles["card__flex"]}>
                <input
                  className={`${styles["input__name"]} ${
                    editMode ? styles["edit-mode"] : ""
                  }`}
                  placeholder="VORNAME"
                  disabled={!editMode && !addSBKUser}
                  name="firstName"
                  value={user.firstName}
                  onChange={handleChange}
                />
                <input
                  className={`${styles["input__name"]} ${
                    editMode ? styles["edit-mode"] : ""
                  }`}
                  placeholder="NACHNAME"
                  disabled={!editMode && !addSBKUser}
                  name="lastName"
                  value={user.lastName}
                  onChange={handleChange}
                />
              </div>
              <input
                className={`${styles["input__mail"]} ${
                  editMode ? styles["edit-mode"] : ""
                }`}
                placeholder="E-MAIL"
                disabled={!editMode && !addSBKUser}
                name="email"
                value={user.email}
                onChange={handleChange}
              />
            </div>

            <TabTypeSelection
              label="Rolle"
              tabs={roles}
              changeActiveTab={
                editMode || addSBKUser ? handleRoleChange : () => {}
              }
              editMode={editMode}
              activeTab={mapRoleDisplay(user.roles?.[0] ?? "")}
            />

            {!isCustomerAdmin && (
              <MultiDocumentUploader
                editMode={editMode}
                addSBKUser={addSBKUser}
                label="Zertifikat"
                placeholder="Dokument hochladen"
                initialFiles={initialFiles}
                onFilesSelect={
                  editMode || addSBKUser ? handleMultipleFilesUpload : () => {}
                }
                onDeleteDocuments={
                  editMode || addSBKUser ? deleteDocuments : () => {}
                }
              />
            )}
            {isCustomerAdmin && user.roles?.[0] === "Objektbetreuer" && (
              <MultipleObjectSelect
                selectedObjects={selectedProperties}
                setSelectedObjects={setSelectedProperties}
                onPlusIconClick={() => {
                  setChoose(true);
                }}
                editMode={editMode}
                addUser={addSBKUser}
              />
            )}

            {(editMode || addSBKUser) && (
              <FloatingButtons>
                {editMode &&
                  userState.id !== customerAdmin?.currentUser?.loginId && (
                    <DeleteIcon
                      color="red"
                      onClick={() => {
                        setDeleteUserModal(true);
                      }}
                    />
                  )}
                <CustomButton
                  loading={loading}
                  color={isFormValid ? "green" : "lightGrayNew"}
                  onClick={isFormValid ? handleConfirmClick : undefined}
                  width={80}
                >
                  {t("Confirm")}
                </CustomButton>
              </FloatingButtons>
            )}

            {!editMode && !addSBKUser && !deactivated && (
              <FloatingButtons justify="right">
                <EditUserIcon
                  onClick={() => {
                    setEditMode(true);
                  }}
                />
              </FloatingButtons>
            )}

            {deactivated && (
              <FloatingButtons>
                <CustomButton
                  color={"orange"}
                  width={60}
                  onClick={handleReactivateModalOpen}
                >
                  {t("Restore")}
                </CustomButton>
              </FloatingButtons>
            )}
          </div>
        )}

        {choose && (
          <ChooseMultipleObject
            onConfirm={(selected: any) => {
              setChoose(false);
              setSelectedProperties(selected);
            }}
            selected={selectedProperties}
          />
        )}
      </div>
    </AppLayout>
  );
};
